import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { staffApi } from "@client/api/staff";
import type { Question } from "@client/api/types";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { useConfirm } from "@client/components/confirm-dialog";
import { Input } from "@client/components/ui/input";
import { Textarea } from "@client/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@client/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@client/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@client/components/ui/tabs";
import {
  ChevronDown,
  ChevronUp,
  Languages,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Plus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/questions")({
  component: QuestionsPage,
});

const PAGE_SIZE = 20;

function QuestionsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [categoryId, setCategoryId] = useState<string>("");
  const [filter, setFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Creation form state
  const [createActiveTab, setCreateActiveTab] = useState("en");
  const [text, setText] = useState("");
  const [textFr, setTextFr] = useState("");
  const [textEs, setTextEs] = useState("");
  const [correct, setCorrect] = useState<"true" | "false">("true");

  // Edit dialog state
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editActiveTab, setEditActiveTab] = useState("en");
  const [editText, setEditText] = useState("");
  const [editTextFr, setEditTextFr] = useState("");
  const [editTextEs, setEditTextEs] = useState("");
  const [editCorrect, setEditCorrect] = useState<"true" | "false">("true");

  // Expanded translation preview state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: staffApi.categories,
  });

  const { data: questions, isLoading } = useQuery({
    queryKey: ["questions", filter],
    queryFn: () => staffApi.questions(filter || undefined),
    enabled: filter !== "",
  });

  const create = useMutation({
    mutationFn: async () =>
      staffApi.createQuestion({
        categoryId,
        text: text.trim(),
        textFr: textFr.trim() || undefined,
        textEs: textEs.trim() || undefined,
        correctAnswer: correct === "true",
      }),
    onSuccess: () => {
      toast.success("Question created successfully");
      setText("");
      setTextFr("");
      setTextEs("");
      qc.invalidateQueries({ queryKey: ["questions"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: async () => {
      if (!editingQuestion) return;
      return staffApi.updateQuestion(editingQuestion.id, {
        categoryId: editCategoryId || undefined,
        text: editText.trim(),
        textFr: editTextFr.trim() || undefined,
        textEs: editTextEs.trim() || undefined,
        correctAnswer: editCorrect === "true",
      });
    },
    onSuccess: (res) => {
      if (!res?.question || !editingQuestion) return;
      toast.success("Question & Answer key updated successfully");
      // Update in-place in the cache to preserve order — do NOT invalidate
      qc.setQueryData<Question[]>(["questions", filter], (current) =>
        current
          ? current.map((q) =>
              q.id === editingQuestion.id ? { ...q, ...res.question } : q,
            )
          : current,
      );
      setEditingQuestion(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: staffApi.deleteQuestion,
    onSuccess: () => {
      toast.success("Question deleted");
      qc.invalidateQueries({ queryKey: ["questions"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const startEdit = (q: Question) => {
    setEditingQuestion(q);
    setEditCategoryId(q.category_id);
    setEditText(q.text || "");
    setEditTextFr(q.text_fr || "");
    setEditTextEs(q.text_es || "");
    setEditCorrect(q.correct_answer ? "true" : "false");
    setEditActiveTab("en");
  };

  const categoryName = (id: string) =>
    categories?.find((c) => c.id === id)?.name ?? "—";

  const searchLower = search.toLowerCase();
  const visible = (questions ?? []).filter((q) => {
    return (
      q.text.toLowerCase().includes(searchLower) ||
      (q.text_fr && q.text_fr.toLowerCase().includes(searchLower)) ||
      (q.text_es && q.text_es.toLowerCase().includes(searchLower))
    );
  });

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  // Clamp current page so it never exceeds total pages
  const currentPageClamped = Math.min(Math.max(1, currentPage), totalPages);
  const pageStart = (currentPageClamped - 1) * PAGE_SIZE;
  const pagedQuestions = visible.slice(pageStart, pageStart + PAGE_SIZE);

  // Build a stable window of up to 5 page buttons
  const buildPageNumbers = (): number[] => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    let start = Math.max(1, currentPageClamped - 2);
    const end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };
  const pageNumbers = buildPageNumbers();

  return (
    <StaffShell
      title="Question Bank"
      subtitle="Multilingual WKF Examination Bank (English, French, Spanish) & True/False Key Management"
    >
      <div className="grid gap-6 xl:grid-cols-[400px_1fr]">
        {/* Creation Form */}
        <form
          className="panel space-y-4 p-5 h-fit"
          onSubmit={(e) => {
            e.preventDefault();
            if (!categoryId) {
              toast.error("Please select a category");
              return;
            }
            if (text.trim().length < 5) {
              toast.error("English question statement is required (at least 5 characters)");
              return;
            }
            create.mutate();
          }}
        >
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            <h2 className="hall-heading text-lg font-bold">Add Question</h2>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
              Category
            </label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Select Category" />
              </SelectTrigger>
              <SelectContent>
                {(categories ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Question Statement
              </label>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Languages className="h-3.5 w-3.5" /> 3 Languages
              </span>
            </div>

            <Tabs value={createActiveTab} onValueChange={setCreateActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="en">
                  🇬🇧 English <span className="text-xs text-destructive ml-1">*</span>
                </TabsTrigger>
                <TabsTrigger value="fr">
                  🇫🇷 Français
                </TabsTrigger>
                <TabsTrigger value="es">
                  🇪🇸 Español
                </TabsTrigger>
              </TabsList>

              <TabsContent value="en" className="pt-2">
                <Textarea
                  rows={4}
                  placeholder="Statement in English (e.g., In Kumite, a contestant may...)"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="resize-y"
                />
              </TabsContent>

              <TabsContent value="fr" className="pt-2">
                <Textarea
                  rows={4}
                  placeholder="Énoncé en Français (optionnel)..."
                  value={textFr}
                  onChange={(e) => setTextFr(e.target.value)}
                  className="resize-y"
                />
              </TabsContent>

              <TabsContent value="es" className="pt-2">
                <Textarea
                  rows={4}
                  placeholder="Declaración en Español (opcional)..."
                  value={textEs}
                  onChange={(e) => setTextEs(e.target.value)}
                  className="resize-y"
                />
              </TabsContent>
            </Tabs>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
              Official Answer Key
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCorrect("true")}
                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border text-sm font-semibold transition-all cursor-pointer ${
                  correct === "true"
                    ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-600 dark:text-emerald-400 ring-2 ring-emerald-500/30"
                    : "border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                TRUE
              </button>

              <button
                type="button"
                onClick={() => setCorrect("false")}
                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border text-sm font-semibold transition-all cursor-pointer ${
                  correct === "false"
                    ? "bg-rose-500/15 border-rose-500/50 text-rose-600 dark:text-rose-400 ring-2 ring-rose-500/30"
                    : "border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                <XCircle className="h-4 w-4 text-rose-500" />
                FALSE
              </button>
            </div>
          </div>

          <Button type="submit" className="w-full mt-2" disabled={create.isPending}>
            {create.isPending ? "Adding..." : "Add to Question Bank"}
          </Button>
        </form>

        {/* Questions List Panel */}
        <div className="panel p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <h2 className="hall-heading text-lg font-bold">
                Question Bank
              </h2>
              {filter !== "" && (
                <Badge variant="secondary" className="font-mono text-xs">
                  {visible.length} questions
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {filter !== "" && (
                <Input
                  className="w-48 sm:w-64"
                  placeholder="Search statements…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              )}
              <Select
                value={filter}
                onValueChange={(v) => {
                  setFilter(v);
                  setSearch("");
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-52">
                  <SelectValue placeholder="Select a category…" />
                </SelectTrigger>
                <SelectContent>
                  {(categories ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Question Cards List */}
          <div className="mt-5 space-y-3">
            {filter === "" ? (
              <div className="text-center py-16 text-sm text-muted-foreground rounded-lg border border-dashed border-border">
                <Languages className="mx-auto h-8 w-8 mb-3 opacity-40" />
                Select a category above to view its questions.
              </div>
            ) : isLoading ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                Loading questions...
              </div>
            ) : pagedQuestions.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground rounded-lg border border-dashed border-border">
                No questions found matching your criteria.
              </div>
            ) : (
              pagedQuestions.map((q, idx) => {
                const isExpanded = expandedIds.has(q.id);
                const hasFr = Boolean(q.text_fr && q.text_fr.trim());
                const hasEs = Boolean(q.text_es && q.text_es.trim());
                // Stable absolute number regardless of current page
                const questionNumber = pageStart + idx + 1;

                return (
                  <div
                    key={q.id}
                    className="rounded-xl border border-border/80 bg-card/60 p-4 transition-all hover:border-border hover:bg-card/90"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      {/* Question Body */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className="text-xs font-bold text-muted-foreground font-mono">
                            #{questionNumber}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {categoryName(q.category_id)}
                          </Badge>
                          <Badge
                            className={
                              q.correct_answer
                                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
                                : "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30"
                            }
                          >
                            {q.correct_answer ? "TRUE" : "FALSE"}
                          </Badge>
                          <div className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground">
                            <span className="px-1.5 py-0.5 rounded bg-muted/80 text-foreground font-semibold">
                              EN
                            </span>
                            {hasFr && (
                              <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 font-semibold">
                                FR
                              </span>
                            )}
                            {hasEs && (
                              <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-semibold">
                                ES
                              </span>
                            )}
                          </div>
                        </div>

                        {/* English Main Statement */}
                        <p className="text-sm font-medium text-foreground leading-relaxed">
                          {q.text}
                        </p>

                        {/* Collapsible Translations */}
                        {(hasFr || hasEs) && (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => toggleExpand(q.id)}
                              className="text-xs text-primary/80 hover:text-primary flex items-center gap-1 font-medium cursor-pointer"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUp className="h-3.5 w-3.5" /> Hide
                                  translations
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="h-3.5 w-3.5" /> View
                                  translations (FR, ES)
                                </>
                              )}
                            </button>

                            {isExpanded && (
                              <div className="mt-2 space-y-2 rounded-lg bg-muted/40 p-3 text-xs border border-border/50">
                                {hasFr && (
                                  <div>
                                    <span className="font-semibold text-blue-600 dark:text-blue-400 block mb-0.5">
                                      🇫🇷 Français:
                                    </span>
                                    <p className="text-muted-foreground leading-relaxed">
                                      {q.text_fr}
                                    </p>
                                  </div>
                                )}
                                {hasEs && (
                                  <div>
                                    <span className="font-semibold text-amber-600 dark:text-amber-400 block mb-0.5">
                                      🇪🇸 Español:
                                    </span>
                                    <p className="text-muted-foreground leading-relaxed">
                                      {q.text_es}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 self-end sm:self-start shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(q)}
                          className="h-8 gap-1 text-xs"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                          Edit Question & Answer
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            if (
                              await confirm({
                                title: "Delete question?",
                                description:
                                  "Delete this question? This action cannot be undone.",
                                confirmText: "Delete",
                                destructive: true,
                              })
                            ) {
                              remove.mutate(q.id);
                            }
                          }}
                          disabled={remove.isPending}
                          className="h-8 text-xs text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
              <span className="text-xs text-muted-foreground">
                Page {currentPageClamped} of {totalPages} ({visible.length} total
                questions)
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.max(1, p - 1))
                  }
                  disabled={currentPageClamped <= 1}
                  className="h-8 px-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1">
                  {pageNumbers.map((pageNum) => (
                    <Button
                      key={pageNum}
                      variant={pageNum === currentPageClamped ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className="h-8 w-8 p-0 text-xs"
                    >
                      {pageNum}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPageClamped >= totalPages}
                  className="h-8 px-2"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Question & Correct Answer Modal */}
      <Dialog
        open={Boolean(editingQuestion)}
        onOpenChange={(open) => !open && setEditingQuestion(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-primary" />
              Edit Question & Answer
            </DialogTitle>
            <DialogDescription>
              Update the question statement in English, French, and Spanish, or
              modify the official True/False answer key.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (editText.trim().length < 5) {
                toast.error("English question statement is required");
                return;
              }
              update.mutate();
            }}
            className="space-y-4 py-2"
          >
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                Category
              </label>
              <Select
                value={editCategoryId}
                onValueChange={setEditCategoryId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  {(categories ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                Official Answer Key (Instructor Choice)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setEditCorrect("true")}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border font-bold text-sm transition-all cursor-pointer ${
                    editCorrect === "true"
                      ? "bg-emerald-500/20 border-emerald-500 text-emerald-600 dark:text-emerald-400 ring-2 ring-emerald-500/40 shadow-sm"
                      : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  TRUE
                </button>

                <button
                  type="button"
                  onClick={() => setEditCorrect("false")}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border font-bold text-sm transition-all cursor-pointer ${
                    editCorrect === "false"
                      ? "bg-rose-500/20 border-rose-500 text-rose-600 dark:text-rose-400 ring-2 ring-rose-500/40 shadow-sm"
                      : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  <XCircle className="h-5 w-5 text-rose-500" />
                  FALSE
                </button>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Multilingual Statements
                </label>
              </div>

              <Tabs value={editActiveTab} onValueChange={setEditActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="en">
                    🇬🇧 English <span className="text-xs text-destructive ml-1">*</span>
                  </TabsTrigger>
                  <TabsTrigger value="fr">
                    🇫🇷 Français
                  </TabsTrigger>
                  <TabsTrigger value="es">
                    🇪🇸 Español
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="en" className="pt-2">
                  <Textarea
                    rows={4}
                    placeholder="Statement in English..."
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="resize-y"
                  />
                </TabsContent>

                <TabsContent value="fr" className="pt-2">
                  <Textarea
                    rows={4}
                    placeholder="Énoncé en Français..."
                    value={editTextFr}
                    onChange={(e) => setEditTextFr(e.target.value)}
                    className="resize-y"
                  />
                </TabsContent>

                <TabsContent value="es" className="pt-2">
                  <Textarea
                    rows={4}
                    placeholder="Declaración en Español..."
                    value={editTextEs}
                    onChange={(e) => setEditTextEs(e.target.value)}
                    className="resize-y"
                  />
                </TabsContent>
              </Tabs>
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingQuestion(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
